import {useEffect, useRef, useState} from "react";
import styled from "styled-components";
import {useAppSelector} from "../../app/hooks";
import {profileState} from "../../app/reducers/profile-slice";
import theme from "../../style/theme";
import {SqaureCheckBox} from "../checkbox/SquareCheckBox";
import ExpandLessIcon from "../icons/ExpandLessIcon/ExpandLessIcon";
import ExpandMoreIcon from "../icons/ExpandMoreIcon";
import FilterLineIcon from "../icons/FilterLineIcon";
import FilterOffLineIcon from "../icons/FilterOffLineIcon";
import {Company} from "../../types/company/company";
import {useClickOutside} from "../../utils/click/handle-click";

interface Props {
    onClickFilterMultiSelectItem: (value: Company) => void;
    values: Company[];
}

export const ClientFilterMultiSelect: React.FC<Props> = (
    {
        values,
        onClickFilterMultiSelectItem,
    }
) => {
    const {profile} = useAppSelector(profileState);
    const [clientList, setClientList] = useState<Company[]>([]);
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const names = values.map((company) => company.name);

    const handleClickBox = () => {
        setOpen(!open);
    };

    useClickOutside(ref, () => setOpen(false));

    const initClientList = () => {
        try {
            if (!profile) {
                return;
            }

            // const clientListTemp = profile.clients.map((client) => client.name);
            // clientListTemp.push(profile.company.name);
            // setClientList(clientListTemp);
            const clients = profile.clients.concat(profile.company);
            setClientList(clients);
        } catch (error) {
            console.log(error);
        }
    };

    const candidateMap = (clientList || []).reduce((acc: any, company) => {
        acc[company.name] = company.name;
        return acc;
    }, {});

    const getSelectedItems = () => {
        if (names.length > 2) {
            return `${names[0]}, ${names[1]}...`;
        }
        return values
            .map((value) => {
                return candidateMap[value.name];
            })
            .join(", ");
    };

    const selectedItems = getSelectedItems();

    useEffect(() => {
        initClientList();
    }, []);

    const handleCheckClientItem = (value: string) => {
        const company = clientList.find((company) => company.name === value)
        if (company) {
            onClickFilterMultiSelectItem(company)
        }
    }

    return (
        <div ref={ref}>
            <SelectedItem values={names} onClick={handleClickBox}>
                <LeftSelectedItemIconWrapper>
                    {values.length > 0 ? <FilterLineIcon/> : <FilterOffLineIcon/>}
                </LeftSelectedItemIconWrapper>
                <SelectedItemLabel values={names}>
                    {selectedItems ? `고객사 : ${selectedItems}` : "모든 고객사"}
                </SelectedItemLabel>
                <RightSelectedItemIconWrapper>
                    {open ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                </RightSelectedItemIconWrapper>
            </SelectedItem>
            {open && (
                <MenuItemList>
                    {Object.keys(candidateMap).map((candidate) => {
                        const isSelected = names.includes(candidate);
                        return (
                            <MenuItem
                                key={candidate}
                                selected={isSelected}
                                onClick={() =>
                                    handleCheckClientItem(candidate)
                                }
                            >
                                <LeftMenuItemIconWrapper>
                                    <SqaureCheckBox
                                        checked={isSelected}
                                        fill={theme.palette.primitives.yellow400}
                                    />
                                </LeftMenuItemIconWrapper>
                                <MenuItemLabel>{candidateMap[candidate]}</MenuItemLabel>
                            </MenuItem>
                        );
                    })}
                </MenuItemList>
            )}
        </div>
    );
};

interface SelectedItemProps {
    values: string[];
}

const SelectedItem = styled.div<SelectedItemProps>`
  //width: 180px;
  height: 42px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  border: 1px solid #bec3cb;
  background-color: #ffffff;

  &:hover {
    background-color: #f5f6f9;
  }
`;

const LeftSelectedItemIconWrapper = styled.div`
  width: 36px;
  height: 42px;
  box-sizing: border-box;
  padding: 12px 8px 12px 12px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

interface SelectedItemLabelProps {
    values: string[];
}

const SelectedItemLabel = styled.div<SelectedItemLabelProps>`
  // width: 228px;
  height: 42px;
  font-size: 15px;
  line-height: 18px;
  font-weight: 500;
  color: ${theme.palette.primitives.gray900};
  display: flex;
  align-items: center;
  margin-right: 12px;
`;

const RightSelectedItemIconWrapper = styled.div`
  width: 36px;
  height: 24px;
  box-sizing: border-box;
  padding: 0 12px 0 7px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-left: 1px solid #e0e3e9;
`;

const MenuItemList = styled.div`
  // width: 230px;
  margin: 4px 0;
  padding: 6.5px 0;
  position: absolute;
  background-color: #ffffff;
  border-radius: 8px;
  box-sizing: border-box;
  box-shadow: 0px 6px 8px rgba(0, 0, 0, 0.2);
  border: solid 1px #bec3cb;
  z-index: 500;
  max-height: calc(100vh - 400px);
  overflow-y: scroll;
  overflow-x: hidden;

  /* 스크롤 바 스타일 */

  &::-webkit-scrollbar {
    width: 5px;
    background-color: #ffffff;
  }

  /* 스크롤바 색상 */

  &::-webkit-scrollbar-thumb {
    background-color: #1c2026;
    border-radius: 10px;
  }

  /* 스크롤바가 마우스 오버될 때 색상 */

  &::-webkit-scrollbar-thumb:hover {
    background-color: #bfbfbf;
  }
`;

interface MenuItemProps {
    selected: boolean;
}

const MenuItem = styled.div<MenuItemProps>`
  // width: 228px;
  height: 36px;
  display: flex;
  color: #3f444e;
  align-items: center;
  background-color: ${(props) =>
          props.selected ? theme.palette.primitives.yellow50 : "#ffffff"};

  &:hover {
    background-color: #f5f6f9;
  }

  font-size: 14px;
  font-weight: 500;
`;

const LeftMenuItemIconWrapper = styled.div`
  width: 42px;
  height: 36px;
  box-sizing: border-box;
  padding: 9px 8px 9px 16px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MenuItemLabel = styled.div`
  // width: 228px;
  height: 42px;
  font-size: 14px;
  font-weight: 500;
  color: #3f444e;
  display: flex;
  align-items: center;
  margin-right: 25px;
`;
